import { useState, useRef, useEffect } from 'react';
import { auth, db, storage } from '../lib/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Icon } from '../components/ui/Icon';
import { ref, set, get } from 'firebase/database';
import { ref as storageRef, uploadString, getDownloadURL } from 'firebase/storage';
import { defaultProfileTemplate } from '../lib/constants';
import { QRCodeSVG } from 'qrcode.react';
import { createRoot } from 'react-dom/client';
import { useOnboarding } from '../contexts/OnboardingContext';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { startGuide } = useOnboarding();

  const generateAndUploadQRCode = async (publicUrl: string) => {
    try {
      // Créer un élément temporaire pour le rendu du QR code
      const tempContainer = document.createElement('div');
      document.body.appendChild(tempContainer);

      // Rendre le QR code dans l'élément temporaire
      createRoot(tempContainer).render(
        <QRCodeSVG
          value={publicUrl}
          size={256}
          level="H"
          includeMargin={true}
        />
      );

      // Attendre que le rendu soit terminé
      await new Promise(resolve => setTimeout(resolve, 100));

      // Obtenir le SVG
      const svg = tempContainer.querySelector('svg');
      if (!svg) {
        throw new Error('SVG non trouvé dans le DOM');
      }

      // Convertir le SVG en base64
      const svgData = new XMLSerializer().serializeToString(svg);
      const base64Data = btoa(svgData);
      const dataUrl = `data:image/svg+xml;base64,${base64Data}`;

      // Nettoyer l'élément temporaire
      document.body.removeChild(tempContainer);

      // Créer une référence dans Firebase Storage
      const qrCodeRef = storageRef(storage, `${auth.currentUser?.uid}/qrcodes/qrcode.png`);

      // Uploader le QR code
      await uploadString(qrCodeRef, dataUrl, 'data_url');

      // Obtenir l'URL publique
      const qrCodeUrl = await getDownloadURL(qrCodeRef);

      return qrCodeUrl;
    } catch (error) {
      throw new Error('Erreur lors de la génération du QR code');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        throw new Error('Les mots de passe ne correspondent pas');
      }

      // Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Créer le profil initial avec un username basé sur le timestamp
      const timestamp = Date.now();
      const initialUsername = `user${timestamp}`;
      
      // Générer l'URL publique pour le QR code
      const publicUrl = `${window.location.origin}/p/${initialUsername}`;
      
      // Générer et uploader le QR code
      const qrCodeUrl = await generateAndUploadQRCode(publicUrl);

      // Créer le profil par défaut
      const defaultProfile = {
        prenom: '',
        nom: '',
        email: email,
        telephone: '',
        entreprise: '',
        poste: '',
        siteWeb: '',
        adresse: '',
        ville: '',
        codePostal: '',
        pays: '',
        linkedin: '',
        facebook: '',
        x: '',
        github: '',
        instagram: '',
        youtube: '',
        tiktok: '',
        behance: '',
        dribbble: '',
        medium: '',
        discord: '',
        whatsapp: '',
        telegram: '',
        skype: '',
        username: initialUsername,
        isPublic: true,
        logoUrl: '',
        qrCodeUrl: qrCodeUrl, // URL du QR code
        qrStyle: {
          fgColor: defaultProfileTemplate.qrStyle.fgColor,
          bgColor: defaultProfileTemplate.qrStyle.bgColor,
          style: defaultProfileTemplate.qrStyle.style
        },
        subscription: {
          status: 'inactive',
          trialEndDate: null
        },
        vcardEnabled: false,
        vcardUrl: '',
        wallpaperUrl: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Sauvegarder le profil dans la base de données
      await set(ref(db, `vcards/${user.uid}`), defaultProfile);

      // Sauvegarder le username dans la table des usernames
      await set(ref(db, `usernames/${initialUsername}`), user.uid);

      navigate('/profile');
      
      // Démarrer le guide
      localStorage.removeItem('hasSeenGuide'); // S'assurer que le guide va se lancer
      startGuide();

    } catch (error: any) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Une erreur est survenue lors de la création du compte');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Icon 
            icon={UserPlus} 
            size="xl" 
            className="text-indigo-600 w-16 h-16 p-4 bg-white rounded-full shadow-lg" 
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Créez votre compte
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Déjà un compte ?{' '}
          <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
            Connectez-vous
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="py-8 px-4 shadow-2xl sm:rounded-xl sm:px-10 bg-white/80 backdrop-blur-sm">
          <form className="space-y-6 registration-form" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Input
                label="Adresse email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                leftIcon={Mail}
                placeholder="vous@exemple.com"
                error={error && error.includes('email') ? error : undefined}
                className="w-full bg-white"
                size="lg"
              />

              <Input
                label="Mot de passe"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                leftIcon={Lock}
                rightIcon={showPassword ? EyeOff : Eye}
                error={error && error.includes('mot de passe') ? error : undefined}
                onRightIconClick={() => setShowPassword(!showPassword)}
                placeholder="••••••••"
                className="w-full bg-white"
                size="lg"
              />

              <Input
                label="Confirmer le mot de passe"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                leftIcon={Lock}
                rightIcon={showConfirmPassword ? EyeOff : Eye}
                error={error && error.includes('correspondent') ? error : undefined}
                onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                placeholder="••••••••"
                className="w-full bg-white"
                size="lg"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              isLoading={loading}
              fullWidth
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading ? 'Création...' : 'Créer le compte'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}